import type { CSSProperties } from "react";
import type { ThemeDocument } from "@/api/queries";

type Scheme = "light" | "dark";
type Themed = Record<string, { light: string; dark: string } | undefined>;

/**
 * A miniature of the app in one scheme, painted from a theme document that
 * has not been applied yet.
 *
 * The page's own stylesheet still carries the *current* brand, so the preview
 * re-declares the Tailwind colour properties on its own box from the preview
 * document: every utility inside resolves `var(--color-…)` against these
 * first. That is what lets the light and the dark panel sit side by side
 * whatever scheme the administrator is viewing in — and what makes the
 * preview the derived values from the server, not an approximation of them.
 */
export function BrandPreview({ document, scheme }: { document: ThemeDocument; scheme: Scheme }) {
  const brand = document.brand.color as Themed;
  const platform = document.platform.color as Themed;
  const viz = document.platform.dataViz;
  const pick = (source: Themed, key: string) => source[key]?.[scheme];

  const style = {
    colorScheme: scheme,
    "--color-accent": pick(brand, "accent"),
    "--color-accent-hover": pick(brand, "accentHover"),
    "--color-accent-pressed": pick(brand, "accentPressed"),
    "--color-accent-subtle": pick(brand, "accentSubtle"),
    "--color-on-accent": pick(brand, "onAccent"),
    "--color-bg": pick(platform, "bg"),
    "--color-surface": pick(platform, "surface"),
    "--color-card": pick(platform, "card"),
    "--color-ink": pick(platform, "textPrimary"),
    "--color-ink-secondary": pick(platform, "textSecondary"),
    "--color-ink-disabled": pick(platform, "textDisabled"),
    "--color-border": pick(platform, "border"),
    "--color-hover": pick(platform, "hover"),
    "--color-selected": pick(platform, "selected"),
    "--color-info": pick(platform, "info"),
    "--color-muted": pick(platform, "muted"),
  } as CSSProperties;

  const categorical = viz?.categorical?.[scheme] ?? [];
  const sequential = viz?.sequential?.[scheme] ?? [];
  const name = document.brand.name || "Untitled";

  return (
    <figure
      style={style}
      className="border-border bg-bg rounded-container overflow-hidden border"
      aria-label={`${scheme === "light" ? "Light" : "Dark"} mode preview`}
    >
      <div className="flex min-h-56">
        <div className="bg-surface border-border flex w-32 shrink-0 flex-col gap-1 border-e p-2.5">
          <div className="mb-2 flex items-center gap-1.5">
            <span className="bg-accent text-on-accent type-caption grid size-5 place-items-center rounded-inner">
              {name.slice(0, 1).toUpperCase()}
            </span>
            <span className="type-support text-ink truncate font-semibold">{name}</span>
          </div>
          <span className="bg-selected text-ink type-support rounded-inner px-2 py-1">
            Conversations
          </span>
          <span className="text-ink-secondary type-support rounded-inner px-2 py-1">Reports</span>
          <span className="bg-hover text-ink-secondary type-support rounded-inner px-2 py-1">
            Dashboards
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
          <div className="bg-card border-border rounded-element flex flex-col gap-2.5 border p-3">
            <p className="type-caption text-accent">{name}</p>
            <p className="type-label text-ink">Revenue by region</p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="bg-accent text-on-accent type-support rounded-inner px-2.5 py-1">
                Primary
              </span>
              <span className="bg-accent-hover text-on-accent type-support rounded-inner px-2.5 py-1">
                Hover
              </span>
              <span className="bg-accent-subtle text-accent type-support rounded-inner px-2.5 py-1">
                Subtle
              </span>
              <span className="text-accent type-support underline">A link</span>
            </div>
            <div className="flex h-12 items-end gap-1" aria-hidden="true">
              {categorical.slice(0, 6).map((color, index) => (
                <span
                  key={`${color}-${index}`}
                  className="flex-1 rounded-t-[3px]"
                  style={{ backgroundColor: color, height: `${90 - index * 12}%` }}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="type-caption text-ink-disabled">Chart series</span>
            <div className="flex gap-0.5" aria-hidden="true">
              {categorical.map((color, index) => (
                <span
                  key={`${color}-${index}`}
                  title={`Series ${index + 1}: ${color}`}
                  className="h-3 flex-1 first:rounded-s-full last:rounded-e-full"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <span className="type-caption text-ink-disabled mt-1">Heatmaps and maps</span>
            <div className="flex" aria-hidden="true">
              {sequential.map((color, index) => (
                <span
                  key={`${color}-${index}`}
                  className="h-3 flex-1 first:rounded-s-full last:rounded-e-full"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <figcaption className="bg-surface border-border type-caption text-ink-secondary border-t px-3 py-1.5">
        {scheme === "light" ? "Light mode" : "Dark mode"}
      </figcaption>
    </figure>
  );
}
